// Plain-English copy that surfaces verification flags and failure
// reasons to a non-technical reviewer. The audit framing is intentional:
// each flag describes a property of the receipt in reviewer language.

import type { InclusionProofsSource, VerificationReport } from "@sequesign/lib/types";

export const FLAG_COPY: Record<keyof VerificationReport["flags"], string> = {
  hash_integrity: "Action records and evidence are unmodified since the chain was constructed.",
  sequence_integrity:
    "Actions are in the original order and the chain extension function reproduces every state.",
  schema_valid: "Evidence content matches the declared schemas.",
  workflow_profile_valid: "The action sequence satisfies the declared workflow template.",
  witnessed: "An independent witness countersigned each chain extension.",
  agent_identity_bound: "Each action is signed by the recorded agent identity.",
  policy_bound: "Every action record commits to the same policy context.",
  approval: "An approving party (human or agent) signed off on this work.",
  counterparty: "A counterparty signed an attestation confirming the work.",
  completeness_verified:
    "The witness log confirms the receipt records every action it captured for this chain.",
  inclusion_proofs_verified:
    "Each witness attestation carries a Merkle proof that the witness has sealed it into a published batch root."
};

export const FLAG_PLAIN_NAME: Record<keyof VerificationReport["flags"], string> = {
  hash_integrity: "Records and evidence unmodified",
  sequence_integrity: "Original action order",
  schema_valid: "Evidence matches schema",
  workflow_profile_valid: "Workflow template satisfied",
  witnessed: "Independent witness countersigned",
  agent_identity_bound: "Signed by recorded agent",
  policy_bound: "Same policy context throughout",
  approval: "Approving party signed off",
  counterparty: "Counterparty confirmed the work",
  completeness_verified: "Completeness verified",
  inclusion_proofs_verified: "Inclusion proofs verified"
};

export const FLAG_ORDER: ReadonlyArray<keyof VerificationReport["flags"]> = [
  "hash_integrity",
  "sequence_integrity",
  "schema_valid",
  "workflow_profile_valid",
  "agent_identity_bound",
  "witnessed",
  "policy_bound",
  "approval",
  "counterparty",
  "completeness_verified",
  "inclusion_proofs_verified"
];

// Inclusion proofs are four-valued (passed / partial / not_present / failed)
// rather than boolean-or-null, so the FlagTable consults this map for the
// inclusion_proofs_verified row instead of the generic three-way status.
export const INCLUSION_PROOFS_STATUS: Record<
  "passed" | "partial" | "not_present" | "failed",
  { label: string; className: "verified" | "failed" | "not-requested" }
> = {
  passed: { label: "Verified", className: "verified" },
  partial: { label: "Partially verified", className: "verified" },
  not_present: { label: "Not requested", className: "not-requested" },
  failed: { label: "Failed verification", className: "failed" }
};

// Provenance suffix for the inclusion-proofs flag. Renders next to the
// status label so the reviewer can tell whether the proofs travelled
// with the receipt, came from a sidecar file, or were fetched from the
// witness's online archive. See PLAN.md section 4.23.
export const INCLUSION_PROOFS_SOURCE_LABEL: Record<InclusionProofsSource, string> = {
  embedded: "from receipt",
  sidecar: "from sidecar file",
  fetched_online: "from witness log",
  mixed: "from multiple sources",
  not_available: ""
};

export const LEVEL_LABELS: Record<string, string> = {
  L0_INTEGRITY_ONLY: "L0 Integrity Only",
  L1_WITNESSED: "L1 Witnessed",
  L2_KEY_BOUND: "L2 Key Bound",
  L3_POLICY_BOUND: "L3 Policy Bound",
  NONE: "None"
};

// Badge derivation for the two-badge verification display. Per
// protocol-spec.md 6.3, witnessed is an independent axis from the ladder
// level, so the verifier renders them as two separate badges sourced
// directly from the VerificationReport rather than collapsing witnessing
// into one ordinal value.
//
// "good" is the positive-trust accent (green family); "neutral" is muted,
// no accent. A not-witnessed receipt is neutral, not a failure: a receipt
// can be valid and unwitnessed. The level is a classification, not a
// pass/fail signal, so every level renders neutral; the summary-state
// heading carries the overall verification status.
export type BadgeVariant = "good" | "neutral";

export interface Badge {
  label: string;
  variant: BadgeVariant;
}

export function witnessedBadge(witnessed: boolean): Badge {
  return witnessed
    ? { label: "Witnessed", variant: "good" }
    : { label: "Not witnessed", variant: "neutral" };
}

export function levelBadge(level: string): Badge {
  return { label: LEVEL_LABELS[level] ?? level, variant: "neutral" };
}

export const FAILURE_REASON_EXPLAIN: Record<string, string> = {
  evidence_hash_mismatch:
    "An evidence blob on disk no longer hashes to the value committed inside its action record. The evidence has been modified since the chain was constructed.",
  action_record_hash_mismatch:
    "An action record on disk no longer hashes to the value committed in the receipt envelope. An action record has been modified since the chain was constructed.",
  chain_state_mismatch:
    "An action declares a previous chain state that does not match the recomputed walk. The chain order has been altered.",
  previous_chain_state_mismatch:
    "An action declares a previous chain state that does not match the recomputed walk. The chain order has been altered.",
  final_chain_state_mismatch:
    "The recomputed final chain state does not match the value recorded in the receipt envelope.",
  sequence_mismatch: "An action's sequence number does not match its position in the chain.",
  agent_signature_invalid:
    "An agent attestation signature did not verify against the recorded agent public key.",
  witness_signature_invalid:
    "A witness attestation signature did not verify against the witness public key.",
  schema_hash_mismatch:
    "A declared schema hash does not match the canonical hash of the schema document at that ID. The schema referenced by the receipt is not the schema that produced the receipt.",
  schema_validation_failed: "Evidence content did not validate against its declared schema.",
  profile_hash_mismatch:
    "The declared profile hash does not match the canonical hash of the profile document at that ID.",
  workflow_profile_validation_failed:
    "The action sequence did not satisfy the declared workflow template.",
  policy_binding_failed: "Not every action record commits to the same policy_context_hash.",
  approval_binding_mismatch:
    "An approval attestation is not bound to this receipt's task or to a recorded action type in the chain.",
  counterparty_attestation_binding_mismatch:
    "A counterparty attestation is not bound to this chain, to a recorded action, or to that action's committed evidence.",
  missing_required_attestation: "An attestation required by the workflow template is missing.",
  missing_profile_binding:
    "The receipt declares profile_constrained mode but does not include a profile reference.",
  missing_evidence: "An action references evidence that is not present in the receipt package.",
  malformed_receipt: "The receipt envelope is malformed.",
  delivery_signature_invalid: "A delivery signature did not verify against the witness public key.",
  delivery_record_mismatch:
    "The receipt declares a delivery record that does not match the assembled package."
};

export type SummaryState = "verified" | "failed" | "partial";

export function summaryState(report: VerificationReport): SummaryState {
  if (!report.valid) return "failed";
  if (anyExplicitFailure(report)) return "partial";
  return "verified";
}

function anyExplicitFailure(report: VerificationReport): boolean {
  for (const value of Object.values(report.flags)) {
    if (value === false) return true;
  }
  return false;
}

export function summaryStateLabel(state: SummaryState): string {
  if (state === "verified") return "Verified";
  if (state === "failed") return "Failed verification";
  return "Verified with concerns";
}
